import React, { useCallback, FC } from 'react'
import isNumber from 'lodash/isNumber'
import omit from 'lodash/omit'
import { IModalBaseProps } from 'transfix-ui/components/Modals'
import { TTonuSchema } from './TonuForm/TonuSchema'
import { ITonuSuccessResponse, TonuModal, TTonuSuccessResponse } from './TonuModal'
import { useUpdateCarrierTonuMutation } from './graphql/UpdateCarrierTonuMutation'

interface IEditCarrierTonuModalProps
  extends Omit<IModalBaseProps, 'modalType' | 'open' | 'onSuccess'> {
  defaultValues: Partial<TTonuSchema>
  shipmentId: string
  tonuId: string
  carrierApproved?: boolean
  shipperApprovalState: string
  shipperRejectionReason?: string
  onTonuEdited: () => void
}

export const EditCarrierTonuModal: FC<IEditCarrierTonuModalProps> = ({
  carrierApproved,
  defaultValues: defaultValuesProp,
  onTonuEdited,
  shipmentId,
  shipperApprovalState,
  shipperRejectionReason,
  tonuId,
  ...props
}) => {
  const defaultValues: Partial<TTonuSchema> = {
    type: 'carrier',
    shipperFee: null,
    carrierFee: null,
    description: '',
    internalNotesDescription: '',
    isCustomerRequest: false,
    isOutOfPocket: false,
    shipperApprovalState: 'pending',
    ...defaultValuesProp,
  }

  const [updateCarrierTonu] = useUpdateCarrierTonuMutation({
    refetchQueries: ['ShipmentBounceDetails'],
  })

  const successHandler = useCallback(
    async (result: ITonuSuccessResponse): Promise<TTonuSuccessResponse> => {
      try {
        const response = await updateCarrierTonu({
          variables: {
            input: {
              ...omit(result, ['type', 'shipmentId', 'carrierAssignmentId']),
              carrierTonuId: tonuId,
              shipperFee: isNumber(result.shipperFee) ? result.shipperFee : undefined,
              carrierFee: isNumber(result.carrierFee) ? result.carrierFee : undefined,
            },
          },
        })

        const { data } = response || {}
        const { updateCarrierTonu: queryResponse } = data || {}
        const { errors = [] } = queryResponse || {}

        if (errors.length > 0) {
          return ['error', errors]
        }
        onTonuEdited()

        return ['success', null]
      } catch {
        return [
          'error',
          [
            {
              key: 'general',
              message: 'Something went wrong. Please try again later.',
            },
          ],
        ]
      }
    },
    [updateCarrierTonu, tonuId, onTonuEdited]
  )

  return (
    <TonuModal
      shipmentId={shipmentId}
      carrierApproved={carrierApproved}
      defaultValues={defaultValues}
      onSuccess={successHandler}
      title='Edit Carrier TONU'
      shipperApprovalState={shipperApprovalState}
      shipperRejectionReason={shipperRejectionReason}
      {...props}
    />
  )
}
